<template>
  <div class="place-selection-review h-100">
    <div class="review-left">
      <div class="d-flex align-items-center mb-3">
        <b-button variant="link" class="p-0 mr-3" @click="$emit('back')">
          ← Back
        </b-button>
        <h5 class="mb-0">Review Selected Places ({{ selectedPlaces.length }})</h5>
      </div>

      <div v-if="selectedPlaces.length === 0" class="text-muted">
        No places selected.
      </div>

      <!-- Places to Save: saveable places (drawn / uploaded / CSV), rendered
           above the From Library section. Only shown when at least one
           saveable place exists. -->
      <div v-if="saveablePlaces.length > 0" class="review-section review-section--save mb-4">
        <div class="section-header d-flex align-items-center justify-content-between mb-1">
          <div class="section-title">
            <i class="bi bi-cloud-arrow-up section-icon" aria-hidden="true"></i>
            <h6 class="mb-0">Places to Save ({{ saveCount }})</h6>
          </div>
          <div class="d-flex align-items-center">
            <b-button
              v-if="hasSaveablePlaces"
              variant="outline-primary"
              size="sm"
              class="mr-2"
              :disabled="!canSaveCustomPlaces"
              @click.stop="toggleBulkPanel"
            >
              Bulk edit
            </b-button>
            <b-button
              variant="primary"
              size="sm"
              :disabled="saveButtonDisabled"
              @click.stop="onSaveClick"
            >
              <b-spinner v-if="isSaving" small class="mr-1" />
              {{ saveButtonLabel }}
            </b-button>
          </div>
        </div>
        <p class="section-hint">New places that will be added to your library when you save.</p>

        <!-- No_Tenant_Notice: shown when the account has no NPAAS tenant, so the
             Save and tag actions are disabled. Lives inside Places to Save so it
             only appears when the user has unsaved places selected. Points the
             user to support to add the Saving Places feature to their account. -->
        <b-alert
          v-if="!canSaveCustomPlaces"
          show
          variant="light"
          class="review-no-tenant-notice mb-3"
        >
          <i class="bi bi-info-circle mr-2" aria-hidden="true"></i>
          Saving places isn't enabled for your account. Contact
          <a :href="`mailto:${supportEmail}`">{{ supportEmail }}</a>
          to add the Saving Places feature.
        </b-alert>

        <!-- Bulk_Panel: set category/brand across all saveable places at once.
             Mirrors the From Library library-tag-panel Apply/Cancel pattern. -->
        <div v-if="showBulkPanel" class="bulk-tag-panel">
          <b-form-input
            v-model="bulkCategory"
            placeholder="Category"
            size="sm"
            class="bulk-tag-input"
            :disabled="isAutofillingAddresses"
          />
          <b-form-input
            v-model="bulkBrand"
            placeholder="Brand"
            size="sm"
            class="bulk-tag-input"
            :disabled="isAutofillingAddresses"
          />
          <b-form-checkbox
            v-model="bulkOverwrite"
            class="bulk-tag-overwrite"
            :disabled="isAutofillingAddresses"
          >
            Overwrite existing category/brand values
          </b-form-checkbox>

          <!-- Address autofill: a per-place, location-derived action, kept
               visually distinct from the manual Category/Brand fields above. -->
          <hr class="bulk-divider" />
          <b-form-checkbox
            v-model="bulkAutofillAddress"
            class="bulk-autofill-address"
            :disabled="isAutofillingAddresses"
          >
            Autofill missing addresses with city
          </b-form-checkbox>
          <small class="bulk-autofill-hint d-block">
            Fills each place's blank address from its location. Falls back to the
            country, then the place name, when no city is found.
          </small>

          <div class="bulk-tag-actions">
            <b-button
              variant="primary"
              size="sm"
              class="mr-2"
              :disabled="bulkApplyDisabled"
              @click.stop="onApplyBulkFields"
            >
              <b-spinner v-if="isAutofillingAddresses" small class="mr-1" />
              Apply
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="isAutofillingAddresses"
              @click.stop="cancelBulk"
            >
              Cancel
            </b-button>
          </div>
          <small v-if="bulkError" class="bulk-error d-block mt-1">
            {{ bulkError }}
          </small>
        </div>

        <div
          v-for="group in saveablePlaces"
          :key="group.source"
          class="review-group mb-3"
        >
          <div class="group-header">{{ group.label }} ({{ group.items.length }})</div>
          <div
            v-for="place in group.items"
            :key="place._id"
            class="review-place-row"
            :class="{
              'review-place-row--error': !!place.saveError,
              'review-place-row--editing': isEditingPlace(place._id),
              'review-place-row--clickable': isZoomable(place) && !isEditingPlace(place._id),
              'review-place-row--active': place._id === activePlaceId,
            }"
            :title="isZoomable(place) && !isEditingPlace(place._id) ? 'Click to zoom to this place on the map' : null"
            @click="onRowClick(place)"
          >
            <div class="place-name-edit">
              <template v-if="isEditingPlace(place._id)">
                <div class="review-edit-grid">
                  <b-form-group label="Name" label-size="sm" class="mb-2">
                    <b-form-input
                      v-model="editDraft.name"
                      size="sm"
                    />
                  </b-form-group>
                  <b-form-group label="Address" label-size="sm" class="mb-2">
                    <b-form-input
                      v-model="editDraft.address"
                      size="sm"
                    />
                  </b-form-group>
                  <b-form-group label="Category" label-size="sm" class="mb-2">
                    <b-form-input
                      v-model="editDraft.category"
                      size="sm"
                    />
                  </b-form-group>
                  <b-form-group label="Brand" label-size="sm" class="mb-2">
                    <b-form-input
                      v-model="editDraft.brand"
                      size="sm"
                    />
                  </b-form-group>
                </div>
              </template>
              <template v-else-if="isCsvPlace(place)">
                <div class="review-place-name">{{ place.fileName || place.name || 'CSV upload' }}</div>
                <div class="review-place-meta">
                  {{ csvSummaryText(place) }}
                </div>
                <div
                  v-if="isCsvProcessing(place)"
                  class="review-place-processing d-flex align-items-center mt-1"
                >
                  <b-spinner small class="mr-2" />
                  <span>{{ csvStatusText(place) }}</span>
                </div>
                <!-- Individual places parsed from the CSV, shown beneath the
                     filename so a CSV upload reads like the other upload
                     sources (all places listed, not just a count). -->
                <ul
                  v-if="hasCsvPlaceItems(place)"
                  class="review-csv-place-list"
                >
                  <li
                    v-for="item in csvPlaceItems(place)"
                    :key="item.key"
                    class="review-csv-place-item"
                  >
                    <span class="review-csv-place-item-name">{{ item.name }}</span>
                    <span v-if="item.address" class="review-csv-place-item-address">{{ item.address }}</span>
                  </li>
                  <li
                    v-if="csvHiddenItemCount(place) > 0"
                    class="review-csv-place-item review-csv-place-item--more"
                  >
                    and {{ csvHiddenItemCount(place).toLocaleString() }} more…
                  </li>
                </ul>
              </template>
              <template v-else>
                <div class="review-place-name">{{ place.name || 'Unnamed place' }}</div>
                <div class="review-place-meta" v-if="place.category || place.brand">
                  <span v-if="place.category">Category: {{ place.category }}</span>
                  <span v-if="place.brand" class="ml-2">Brand: {{ place.brand }}</span>
                </div>
              </template>
              <small v-if="place.saveError" class="review-place-error d-block mt-1">
                {{ place.saveError }}
              </small>
            </div>
            <b-badge
              v-if="isUnresolvableLocation(place)"
              variant="light"
              class="review-place-no-location text-muted mr-2"
              title="No location — this place has no map geometry and won't be included"
              aria-label="No location — this place has no map geometry and won't be included"
            >
              <i class="bi bi-geo-alt-fill review-place-no-location-icon" aria-hidden="true"></i>
              No location
            </b-badge>
            <b-badge
              v-if="sourceIndicatorIcon(place)"
              class="review-place-source mr-2"
              :class="`review-place-source--${sourceType(place)}`"
              :title="sourceIndicatorLabel(place)"
              :aria-label="sourceIndicatorLabel(place)"
            >
              <i :class="['bi', sourceIndicatorIcon(place)]" aria-hidden="true"></i>
            </b-badge>
            <b-button
              v-if="isZoomable(place) && !isEditingPlace(place._id)"
              variant="link"
              size="sm"
              class="text-primary ml-1 review-place-zoom"
              title="Zoom to this place on the map"
              @click.stop="$emit('place-click', { id: place._id })"
            >
              <i class="bi bi-geo-alt" aria-hidden="true"></i>
            </b-button>
            <b-badge v-if="place.saveError" variant="danger" class="mr-2">Save failed</b-badge>
            <template v-if="isEditingPlace(place._id)">
              <b-button
                variant="primary"
                size="sm"
                class="mr-2"
                @click.stop="saveEdit(place._id)"
              >
                Apply
              </b-button>
              <b-button
                variant="outline-primary"
                size="sm"
                class="mr-2"
                @click.stop="cancelEdit(place._id)"
              >
                Cancel
              </b-button>
            </template>
            <b-button
              v-else-if="isEditablePlace(place)"
              variant="link"
              size="sm"
              class="text-primary ml-2"
              title="Edit place"
              @click.stop="startEdit(place)"
            >
              <i class="bi bi-pencil"></i>
            </b-button>
            <b-button
              variant="link"
              size="sm"
              class="text-danger ml-2"
              :disabled="isEditingPlace(place._id)"
              @click.stop="removePlace(place._id)"
            >
              ✕
            </b-button>
          </div>
        </div>
      </div>

      <!-- From Library: places already in the user's library. Informational
           only (no Save affordance). Every place here is an existing Azira or
           Custom library place, so these rows are read-only: they carry no
           inline edit affordance (no pencil, no edit-mode grid, no Apply/Cancel)
           and offer only the remove action. Inline editing lives in the Places
           to Save section. Only shown when at least one library place exists. -->
      <div
        v-for="group in libraryDisplayGroups"
        :key="group.key"
        class="review-section review-section--library mb-3"
      >
        <div class="section-header d-flex align-items-center justify-content-between mb-1">
          <div class="section-title">
            <i class="bi bi-bookmark-check section-icon" aria-hidden="true"></i>
            <h6 class="mb-0">{{ group.title }} ({{ group.items.length }})</h6>
          </div>
          <b-button
            v-if="group.taggable"
            variant="outline-primary"
            size="sm"
            :disabled="!canSaveCustomPlaces"
            @click.stop="toggleLibraryTagPanel"
          >
            Add tag
          </b-button>
        </div>
        <p class="section-hint">{{ group.hint }}</p>

        <div v-if="group.taggable && showLibraryTagPanel" class="library-tag-panel">
          <TagInput
            v-model="libraryTags"
            placeholder="Enter tag(s), comma-separated"
            class="library-tag-input"
          />
          <div class="library-tag-actions">
            <b-button
              variant="primary"
              size="sm"
              class="mr-2"
              :disabled="applyLibraryTagDisabled"
              @click.stop="onApplyLibraryTags"
            >
              <b-spinner v-if="isApplyingLibraryTags" small class="mr-1" />
              Apply
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              @click.stop="cancelLibraryTag"
            >
              Cancel
            </b-button>
          </div>
          <small v-if="libraryTagError" class="library-tag-error d-block mt-1">
            {{ libraryTagError }}
          </small>
        </div>

        <div class="review-group mb-3">
          <div
            v-for="place in group.items"
            :key="place._id"
            class="review-place-row"
            :class="{
              'review-place-row--error': !!place.saveError,
              'review-place-row--clickable': isZoomable(place),
              'review-place-row--active': place._id === activePlaceId,
            }"
            :title="isZoomable(place) ? 'Click to zoom to this place on the map' : null"
            @click="onRowClick(place)"
          >
            <div class="place-name-edit">
              <!-- From Library rows are read-only: every place here is an
                   existing Azira or Custom library place, so there is no inline
                   edit mode (name/address/category/brand grid) as there is in
                   Places to Save. -->
              <template v-if="isCsvPlace(place)">
                <div class="review-place-name">{{ place.fileName || place.name || 'CSV upload' }}</div>
                <div class="review-place-meta">
                  {{ csvSummaryText(place) }}
                </div>
                <div
                  v-if="isCsvProcessing(place)"
                  class="review-place-processing d-flex align-items-center mt-1"
                >
                  <b-spinner small class="mr-2" />
                  <span>{{ csvStatusText(place) }}</span>
                </div>
                <!-- Individual places parsed from the CSV, shown beneath the
                     filename so a CSV upload reads like the other upload
                     sources (all places listed, not just a count). -->
                <ul
                  v-if="hasCsvPlaceItems(place)"
                  class="review-csv-place-list"
                >
                  <li
                    v-for="item in csvPlaceItems(place)"
                    :key="item.key"
                    class="review-csv-place-item"
                  >
                    <span class="review-csv-place-item-name">{{ item.name }}</span>
                    <span v-if="item.address" class="review-csv-place-item-address">{{ item.address }}</span>
                  </li>
                  <li
                    v-if="csvHiddenItemCount(place) > 0"
                    class="review-csv-place-item review-csv-place-item--more"
                  >
                    and {{ csvHiddenItemCount(place).toLocaleString() }} more…
                  </li>
                </ul>
              </template>
              <template v-else>
                <div class="review-place-name">{{ place.name || 'Unnamed place' }}</div>
                <div class="review-place-meta" v-if="place.category || place.brand">
                  <span v-if="place.category">Category: {{ place.category }}</span>
                  <span v-if="place.brand" class="ml-2">Brand: {{ place.brand }}</span>
                </div>
                <div
                  v-if="placeTags(place).length > 0"
                  class="review-place-tags"
                >
                  <span
                    v-for="(tag, tagIdx) in placeTags(place)"
                    :key="tagIdx"
                    class="review-place-tag"
                  >{{ tag }}</span>
                </div>
              </template>
              <small v-if="place.saveError" class="review-place-error d-block mt-1">
                {{ place.saveError }}
              </small>
            </div>
            <b-badge
              v-if="isUnresolvableLocation(place)"
              variant="light"
              class="review-place-no-location text-muted mr-2"
              title="No location — this place has no map geometry and won't be included"
              aria-label="No location — this place has no map geometry and won't be included"
            >
              <i class="bi bi-geo-alt-fill review-place-no-location-icon" aria-hidden="true"></i>
              No location
            </b-badge>
            <b-badge
              v-if="sourceIndicatorIcon(place)"
              class="review-place-source mr-2"
              :class="`review-place-source--${sourceType(place)}`"
              :title="sourceIndicatorLabel(place)"
              :aria-label="sourceIndicatorLabel(place)"
            >
              <i :class="['bi', sourceIndicatorIcon(place)]" aria-hidden="true"></i>
            </b-badge>
            <b-button
              v-if="isZoomable(place)"
              variant="link"
              size="sm"
              class="text-primary ml-1 review-place-zoom"
              title="Zoom to this place on the map"
              @click.stop="$emit('place-click', { id: place._id })"
            >
              <i class="bi bi-geo-alt" aria-hidden="true"></i>
            </b-button>
            <b-badge v-if="place.saveError" variant="danger" class="mr-2">Save failed</b-badge>
            <!-- No edit affordance in From Library: these rows are existing
                 library places and are never editable here, so only the remove
                 action is offered. Inline editing lives in Places to Save. -->
            <b-button
              variant="link"
              size="sm"
              class="text-danger ml-2"
              @click.stop="removePlace(place._id)"
            >
              ✕
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  partitionPlaces,
  partitionLibraryPlaces,
  saveCount as saveCountHelper,
  saveButtonLabel as saveButtonLabelHelper,
  isEmptyValue,
  validateSaveablePlaces,
  selectSaveableNeedingAddress,
  resolveAutofillAddress,
} from './placeSelectionSaveHelpers';
import { sourceType, hasResolvableLocation } from './placeSelectionBuilderHelpers';
import { reverseGeocodePlaceLabel } from '@/services/dataExplorer/reverseGeocodeService';
import { Constants } from '@/services/constants';
import TagInput from '@/components/common/TagInput.vue';

export default {
  name: 'PlaceSelectionReview',
  components: { TagInput },
  props: {
    editingPlaceId: {
      type: String,
      default: null,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    canSaveCustomPlaces: {
      type: Boolean,
      default: false,
    },
    isApplyingLibraryTags: {
      type: Boolean,
      default: false,
    },
    libraryTagError: {
      type: String,
      default: '',
    },
    libraryTagResetToken: {
      type: [String, Number],
      default: '',
    },
    activePlaceId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      editDraft: {
        name: '',
        address: '',
        category: '',
        brand: '',
      },
      showLibraryTagPanel: false,
      libraryTags: [],
      // Bulk_Panel state (category/brand bulk update for saveable places).
      showBulkPanel: false, // Bulk_Panel collapsible open state
      bulkCategory: '', // category input
      bulkBrand: '', // brand input
      bulkOverwrite: false, // Overwrite_Toggle (category/brand only)
      bulkAutofillAddress: false, // Autofill missing addresses with city
      isAutofillingAddresses: false, // in-flight flag while reverse-geocoding
      bulkError: '', // inline guard message (open-edit)
    };
  },
  computed: {
    // Support address surfaced in the No_Tenant_Notice so a user without a
    // NPAAS tenant knows who to contact to enable the Saving Places feature.
    supportEmail() {
      return Constants.support_email;
    },
    selectedPlaces() {
      return this.$sessionStore.state.placeSelection.selectedPlaces;
    },
    // Places to Save, preserving per-source sub-groups (Drawn / Uploaded / CSV Upload).
    saveablePlaces() {
      return partitionPlaces(this.selectedPlaces).saveable;
    },
    // From Library: everything not saveable (library, null, missing, unknown source).
    libraryPlaces() {
      return partitionPlaces(this.selectedPlaces).library;
    },
    // From Library, split by origin: Azira catalog places vs the account's
    // Custom places (classified via placematrix_id, mirroring the backend rule).
    libraryGroups() {
      return partitionLibraryPlaces(this.libraryPlaces);
    },
    aziraLibraryPlaces() {
      return this.libraryGroups.azira;
    },
    customLibraryPlaces() {
      return this.libraryGroups.custom;
    },
    // Ordered, non-empty library cards. Custom renders first and is the only
    // taggable group (Azira catalog places cannot be tagged), so it carries the
    // Add-tag affordance; Azira is informational only. Each entry is
    // { key, title, hint, taggable, items }.
    libraryDisplayGroups() {
      return [
        {
          key: 'custom',
          title: 'Custom',
          hint: 'Custom places you have saved to the library. Tags can be added to these places.',
          taggable: true,
          items: this.customLibraryPlaces,
        },
        {
          key: 'azira',
          title: 'Azira',
          hint: 'Azira catalog places already saved in the library.',
          taggable: false,
          items: this.aziraLibraryPlaces,
        },
      ].filter((group) => group.items.length > 0);
    },
    saveCount() {
      return saveCountHelper(this.selectedPlaces);
    },
    hasSaveErrors() {
      return this.saveablePlaces.some((group) =>
        group.items.some((place) => !!place.saveError));
    },
    // True while any saveable CSV place is still being polled for processing
    // status. Its row stays in "Places to Save" with a spinner, and Save is
    // disabled so the user cannot re-register the same upload mid-poll.
    hasProcessingCsvPlaces() {
      return this.selectedPlaces.some(
        (p) => p && p._source === 'csv' && p.csvProcessing === true,
      );
    },
    saveButtonDisabled() {
      return (
        this.isSaving ||
        this.hasProcessingCsvPlaces ||
        !!this.editingPlaceId ||
        !this.canSaveCustomPlaces
      );
    },
    saveButtonLabel() {
      return saveButtonLabelHelper(this.saveCount, this.hasSaveErrors, this.isSaving);
    },
    // The shared TagInput already trims, drops empties, and dedupes on commit,
    // so libraryTags is a clean array. This computed is a defensive
    // case-sensitive re-dedupe (first occurrence, entered order preserved) so
    // the emitted apply-library-tags payload never carries duplicates even if
    // the array is seeded oddly.
    parsedLibraryTags() {
      const seen = new Set();
      return (this.libraryTags || []).filter((tag) => {
        if (seen.has(tag)) return false;
        seen.add(tag);
        return true;
      });
    },
    applyLibraryTagDisabled() {
      return this.parsedLibraryTags.length === 0 || this.isApplyingLibraryTags;
    },
    // True when at least one saveable place exists; drives the Bulk_Panel
    // affordance in the Save_Section (Req 4.1).
    hasSaveablePlaces() {
      return this.saveablePlaces.length > 0;
    },
    // True when there is nothing for an Apply to do: both category/brand inputs
    // are Empty_Values AND the address autofill is not requested. Uses the
    // shared isEmptyValue helper for consistent semantics.
    bulkInputsEmpty() {
      return (
        isEmptyValue(this.bulkCategory) &&
        isEmptyValue(this.bulkBrand) &&
        !this.bulkAutofillAddress
      );
    },
    // Guards the Apply control: disabled when there is nothing to apply, no
    // saveable place exists, an inline row edit is open, or an autofill is
    // already in flight. The address-autofill checkbox is an independent action
    // — ticking it alone (with empty category/brand) enables Apply.
    bulkApplyDisabled() {
      return (
        this.bulkInputsEmpty ||
        this.saveCount === 0 ||
        !!this.editingPlaceId ||
        this.isAutofillingAddresses
      );
    },
  },
  watch: {
    libraryTagResetToken() {
      this.libraryTags = [];
      this.showLibraryTagPanel = false;
    },
  },
  methods: {
    isEditingPlace(placeId) {
      return this.editingPlaceId === placeId;
    },
    isCsvPlace(place) {
      return !!place && place._source === 'csv';
    },
    isEditablePlace(place) {
      // Places pulled "From Library" (_source === 'library') come from the
      // canonical Places catalog and are not editable here; hide their edit
      // affordance. Drawn/uploaded/CSV places remain editable.
      return !!place && place._source !== 'library';
    },
    isZoomable(place) {
      // A place can be focused on the map when it has a geometry, or (for CSV
      // uploads) at least one preview feature to bound.
      if (!place) return false;
      if (place.geometry) return true;
      return Array.isArray(place.previewFeatures) && place.previewFeatures.length > 0;
    },
    // True when the place has no location the Builder → Job_Request sync can
    // resolve. Uses the same `hasResolvableLocation` predicate the sync uses to
    // exclude the place, so this indicator and the sync's exclusion set stay
    // consistent (Req 6.1). Because it is derived from `hasResolvableLocation`,
    // the indicator clears automatically once the place gains geometry — e.g.
    // via updateSelectedPlaceGeometry (Req 6.4).
    isUnresolvableLocation(place) {
      return !hasResolvableLocation(place);
    },
    // Source_Type of a Place_Item (`searched` / `drawn` / `uploaded` / null),
    // delegated to the shared helper so the vocabulary mapping stays in one
    // place.
    sourceType(place) {
      return sourceType(place);
    },
    // Bootstrap-icon class for the per-row Source_Indicator, distinct per
    // Source_Type. Returns null when the source cannot be classified so the
    // badge is not rendered.
    sourceIndicatorIcon(place) {
      switch (sourceType(place)) {
        case 'searched':
          return 'bi-search';
        case 'drawn':
          return 'bi-pencil-square';
        case 'uploaded':
          return 'bi-cloud-arrow-up';
        default:
          return null;
      }
    },
    // Human-readable label for the Source_Indicator (used as tooltip/aria text).
    sourceIndicatorLabel(place) {
      switch (sourceType(place)) {
        case 'searched':
          return 'Searched';
        case 'drawn':
          return 'Drawn';
        case 'uploaded':
          return 'Uploaded';
        default:
          return '';
      }
    },
    onRowClick(place) {
      // Ignore clicks while a row is being edited so the inline form stays usable.
      if (!place || this.isEditingPlace(place._id)) return;
      if (!this.isZoomable(place)) return;
      this.$emit('place-click', { id: place._id });
    },
    csvSummaryText(place) {
      const count = (place.csvSummary && place.csvSummary.count) || 0;
      const label = count === 1 ? 'place' : 'places';
      return `${count.toLocaleString()} ${label}`;
    },
    // The individual places parsed from a CSV upload, derived from the place's
    // `previewFeatures` (the same capped feature list built when the CSV was
    // added). Mirrors the per-feature rows the other upload sources render, so a
    // CSV upload shows every place alongside its filename rather than just a
    // count. Each entry carries a display `name` and `address` (from the feature
    // properties). Returns an empty array when no preview features exist.
    csvPlaceItems(place) {
      const features = place && Array.isArray(place.previewFeatures)
        ? place.previewFeatures
        : [];
      return features.map((feature, index) => {
        const props = (feature && feature.properties) || {};
        const name = `${props.name || ''}`.trim();
        const address = `${props.address || ''}`.trim();
        return {
          key: `${place._id}-csv-${index}`,
          name: name || 'Unnamed place',
          address,
        };
      });
    },
    // True when the CSV upload has any preview places to render individually and
    // it is not mid-processing (during a poll the row shows a live status
    // indicator instead of the parsed list).
    hasCsvPlaceItems(place) {
      return !this.isCsvProcessing(place) && this.csvPlaceItems(place).length > 0;
    },
    // Count of individual CSV places not shown because the preview was capped.
    // Lets the UI note "and N more…" without listing every row for large files.
    csvHiddenItemCount(place) {
      const total = (place && place.csvSummary && place.csvSummary.count) || 0;
      const shown = this.csvPlaceItems(place).length;
      return Math.max(0, total - shown);
    },
    // True while a registered CSV upload is being polled for processing status.
    // Such a place stays in "Places to Save" with a live processing indicator.
    isCsvProcessing(place) {
      return !!place && place._source === 'csv' && place.csvProcessing === true;
    },
    // The live processing/status text for a CSV place being polled. Falls back
    // to a generic processing message when no message has been set yet.
    csvStatusText(place) {
      return (place && place.csvStatusMessage) || 'Processing your uploaded places…';
    },
    // Existing tags for a place. The store normalizes the upstream
    // `labels`/`tags` onto the canonical `labels` field (toPlacePayload), so
    // read that first and fall back to `tags` defensively. Trims, drops
    // empties, and coerces to strings so the chips render cleanly. Returns an
    // empty array when the place carries no tags.
    placeTags(place) {
      const raw = place && (Array.isArray(place.labels) ? place.labels : place.tags);
      if (!Array.isArray(raw)) return [];
      return raw
        .map((tag) => `${tag}`.trim())
        .filter((tag) => tag !== '');
    },
    startEdit(place) {
      if (!place || !place._id) return;
      this.editDraft = {
        name: place.name || '',
        address: place.address || '',
        category: place.category || '',
        brand: place.brand || '',
      };
      this.$emit('edit-start', { id: place._id });
    },
    saveEdit(placeId) {
      if (!this.isEditingPlace(placeId)) return;
      this.$emit('edit-save', {
        id: placeId,
        name: (this.editDraft.name || '').trim(),
        address: (this.editDraft.address || '').trim(),
        category: (this.editDraft.category || '').trim(),
        brand: (this.editDraft.brand || '').trim(),
      });
    },
    cancelEdit(placeId) {
      if (!this.isEditingPlace(placeId)) return;
      this.$emit('edit-cancel', { id: placeId });
      this.editDraft = {
        name: '',
        address: '',
        category: '',
        brand: '',
      };
    },
    removePlace(id) {
      this.$sessionStore.commit('removeSelectedPlace', id);
    },
    onSaveClick() {
      // Ignore activation while disabled (saving in progress, an active edit,
      // or the account lacks save access) so no event is emitted.
      if (this.saveButtonDisabled) return;
      // Validate Required_Fields on every Saveable_Place before starting the
      // save (Req 1.1, 3.1). Non-saveable (library) rows are never validated.
      const result = validateSaveablePlaces(this.selectedPlaces);
      if (!result.valid) {
        // Block the save entirely: surface per-row messages via the shared
        // error channel and do NOT emit, so no persistence is attempted
        // (Req 1.2, 1.3, 2.1, 2.2).
        this.$sessionStore.commit('setSelectedPlaceSaveErrors', {
          failedIds: result.failedIds,
          messagesById: result.messagesById,
        });
        return;
      }
      // Clear any prior validation Row_Errors before saving (Req 2.3, 2.4).
      this.$sessionStore.commit('setSelectedPlaceSaveErrors', { failedIds: [] });
      // Emit exactly one save-custom-places event with no payload; the modal
      // owns the persistence work (Req 1.6).
      this.$emit('save-custom-places');
    },
    toggleLibraryTagPanel() {
      // Toggle the inline tag-entry panel. Opening presents an empty input
      // with Apply + Cancel; clicking while open closes it.
      this.showLibraryTagPanel = !this.showLibraryTagPanel;
    },
    onApplyLibraryTags() {
      // Ignore activation while disabled (no parsed tags or an apply is in
      // progress) so no event is emitted and panel state is unchanged.
      if (this.applyLibraryTagDisabled) return;
      // Emit exactly one apply-library-tags event with the parsed tag list;
      // the modal owns the persistence work.
      this.$emit('apply-library-tags', { tags: this.parsedLibraryTags });
    },
    cancelLibraryTag() {
      this.libraryTags = [];
      this.showLibraryTagPanel = false;
    },
    toggleBulkPanel() {
      // Toggle the inline bulk category/brand panel. Opening presents empty
      // inputs with Apply + Cancel; clicking while open closes it.
      this.showBulkPanel = !this.showBulkPanel;
    },
    cancelBulk() {
      // Reset the Bulk_Panel inputs, toggles, and guard message, then close the
      // panel without committing anything (Req 4.6). Never called mid-autofill
      // (Cancel is disabled while in flight).
      this.bulkCategory = '';
      this.bulkBrand = '';
      this.bulkOverwrite = false;
      this.bulkAutofillAddress = false;
      this.bulkError = '';
      this.showBulkPanel = false;
    },
    async onApplyBulkFields() {
      // Refuse to apply while an inline row edit is open; surface a guard
      // message and commit nothing (Req 6.3).
      if (this.editingPlaceId) {
        this.bulkError = 'Finish the open place edit before applying bulk values.';
        return;
      }
      // Ignore activation while disabled (nothing to apply, no saveable places,
      // or an autofill already running) so state is left untouched.
      if (this.bulkApplyDisabled) return;

      // Apply the manual category/brand bulk update first. The mutation is a
      // no-op when both inputs are empty (autofill-only Apply), so this is safe
      // to run unconditionally.
      this.$sessionStore.commit('bulkUpdateSaveableFields', {
        category: this.bulkCategory,
        brand: this.bulkBrand,
        overwrite: this.bulkOverwrite,
      });

      // When requested, autofill blank addresses on saveable draw/upload places
      // by reverse-geocoding each place's location (city -> country -> name).
      if (this.bulkAutofillAddress) {
        await this.autofillMissingAddresses();
      }

      this.cancelBulk();
    },
    // Reverse-geocode every saveable draw/upload place with a blank address and
    // commit the resolved address. Best-effort per place: a failed lookup falls
    // through the city -> country -> place-name chain, so every eligible place
    // ends up with a non-empty address. Runs sequentially to avoid hammering
    // the geocoder for large selections.
    async autofillMissingAddresses() {
      const targets = selectSaveableNeedingAddress(this.selectedPlaces);
      if (!targets.length) return;

      this.isAutofillingAddresses = true;
      try {
        for (const place of targets) {
          const center = Array.isArray(place.centerLngLat) ? place.centerLngLat : [];
          const [lon, lat] = center.length === 2 ? center : [null, null];

          let city = null;
          let country = null;
          if (lon != null && lat != null) {
            const label = await reverseGeocodePlaceLabel(lon, lat);
            city = label.city;
            country = label.country;
          }

          const address = resolveAutofillAddress({
            city,
            country,
            name: place.name,
          });
          if (address) {
            this.$sessionStore.commit('updateSelectedPlaceFields', {
              id: place._id,
              address,
            });
          }
        }
      } finally {
        this.isAutofillingAddresses = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.place-selection-review {
  height: 100%;
  // Let the single child (.review-left) own the scroll: constrain this root so
  // the child's height: 100% resolves against a bounded box instead of growing
  // to fit every row.
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.review-left {
  width: 100%;
  // Fill the flex parent and cap growth so overflow-y actually engages. Without
  // a bounded height here, the list expands to its natural size and no
  // scrollbar appears no matter how the ancestors are sized.
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem;

  // Card container shared by both review sections. Each section reads as a
  // distinct bordered panel rather than a run of free-floating rows.
  .review-section {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.85rem 0.9rem;
    background: #fff;

    // Places to Save is the actionable section: subtle primary tint + accent.
    &--save {
      border-color: rgba(79, 20, 220, 0.25);
      background: rgba(79, 20, 220, 0.035);
      border-left: 3px solid #4f14dc;

      .section-icon {
        color: #4f14dc;
      }
    }

    // From Library is informational: neutral gray, no accent pull.
    &--library {
      border-color: #e2e8f0;
      background: #f8fafc;
      border-left: 3px solid #b7c1d3;

      .section-icon {
        color: #6e84a3;
      }
    }
  }

  .section-title {
    display: flex;
    align-items: center;

    .section-icon {
      font-size: 1rem;
      margin-right: 0.5rem;
      line-height: 1;
    }

    h6 {
      font-weight: 600;
      color: #12254b;
    }
  }

  // No_Tenant_Notice: a quiet, informational hint rather than a loud warning.
  // Muted neutral surface with small text so it sits calmly inside Places to
  // Save instead of pulling focus from the Save action.
  .review-no-tenant-notice {
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #6e84a3;
    font-size: 0.75rem;
    padding: 0.5rem 0.65rem;

    a {
      color: #4f14dc;
    }
  }

  .section-hint {
    margin: 0 0 0.75rem;
    color: #6e84a3;
    font-size: 0.75rem;
    line-height: 1.3;
  }

  .group-header {
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6e84a3;
    padding: 0.25rem 0;
    border-bottom: 1px solid #e2e8f0;
    margin-bottom: 0.5rem;
  }

  .review-place-row {
    display: flex;
    align-items: flex-start;
    padding: 0.25rem 0;
    border-radius: 4px;

    .place-name-edit {
      flex: 1;
    }

    &--clickable {
      cursor: pointer;

      &:hover {
        background: #eff6ff;
      }
    }

    // Active_Place highlight: the row currently focused on the Builder_Map.
    &--active {
      background: rgba(79, 20, 220, 0.08);
      box-shadow: inset 3px 0 0 #4f14dc;
    }
  }

  // Per-row Source_Indicator: a small icon badge whose colour is distinct per
  // Source_Type (searched / drawn / uploaded).
  .review-place-source {
    display: inline-flex;
    align-items: center;
    margin-top: 0.35rem;
    padding: 0.25rem 0.4rem;
    font-size: 0.7rem;
    line-height: 1;

    &--searched {
      color: #0b6bcb;
      background: rgba(11, 107, 203, 0.12);
    }

    &--drawn {
      color: #b26a00;
      background: rgba(178, 106, 0, 0.12);
    }

    &--uploaded {
      color: #157347;
      background: rgba(21, 115, 71, 0.12);
    }
  }

  // Per-row No_Location indicator: a small muted badge shown when the place has
  // no resolvable geometry, so it will be excluded from the Job_Request sync.
  .review-place-no-location {
    display: inline-flex;
    align-items: center;
    margin-top: 0.35rem;
    padding: 0.25rem 0.4rem;
    font-size: 0.7rem;
    line-height: 1;
    color: #6e84a3;
    background: #eef2f7;

    .review-place-no-location-icon {
      margin-right: 0.25rem;
      opacity: 0.7;
    }
  }

  // Per-row Zoom_Affordance: recentre/zoom the map onto this place.
  .review-place-zoom {
    margin-top: 0.2rem;
    padding: 0.1rem 0.35rem;
    line-height: 1;
  }

  .review-place-name {
    color: #12254b;
    font-weight: 500;
    line-height: 1.2;
    margin-top: 0.35rem;
  }

  .review-place-meta {
    margin-top: 0.2rem;
    color: #6e84a3;
    font-size: 0.78rem;
    line-height: 1.2;
  }

  // Live processing indicator for a CSV upload whose library job is being
  // polled. Sits under the CSV summary line while the row remains in
  // "Places to Save".
  .review-place-processing {
    color: #4c6fff;
    font-size: 0.78rem;
    line-height: 1.2;
  }

  // Individual CSV places listed beneath the filename/summary, so a CSV upload
  // presents every parsed place like the other upload sources do.
  .review-csv-place-list {
    list-style: none;
    margin: 0.35rem 0 0;
    padding: 0 0 0 0.75rem;
    border-left: 2px solid #e2e8f0;
  }

  .review-csv-place-item {
    display: flex;
    flex-direction: column;
    padding: 0.2rem 0;
    line-height: 1.2;

    &--more {
      color: #6e84a3;
      font-size: 0.75rem;
      font-style: italic;
    }
  }

  .review-csv-place-item-name {
    color: #12254b;
    font-size: 0.8rem;
    font-weight: 500;
  }

  .review-csv-place-item-address {
    color: #6e84a3;
    font-size: 0.75rem;
  }

  // Existing-tag chips shown on library rows. Mirrors the green pill styling
  // used for tags in the Place details modal (.group-badge) so tags read
  // consistently across the app.
  .review-place-tags {
    display: flex;
    flex-wrap: wrap;
    margin-top: 0.3rem;
  }

  .review-place-tag {
    background: #0ea26d;
    border-radius: 999px;
    color: #ffffff;
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1;
    margin-bottom: 0.25rem;
    margin-right: 0.35rem;
    padding: 0.2rem 0.6rem;
  }

  .review-edit-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0;
  }

  .review-place-row--error {
    background: rgba(220, 53, 69, 0.06);
    border-radius: 6px;
    padding: 0.4rem 0.35rem;
  }

  .review-place-row--editing {
    background: rgba(79, 20, 220, 0.08);
    border: 1px solid rgba(79, 20, 220, 0.35);
    border-radius: 6px;
    padding: 0.4rem 0.35rem;
    box-shadow: 0 0 0 2px rgba(79, 20, 220, 0.12);
  }

  .review-place-error {
    color: #b02a37;
    font-size: 0.75rem;
    line-height: 1.2;
  }

  .library-tag-panel {
    margin: 0 0 0.75rem;
    padding: 0.6rem;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #fff;

    .library-tag-input {
      margin-bottom: 0.5rem;
    }

    .library-tag-actions {
      display: flex;
      align-items: center;
    }

    .library-tag-error {
      color: #b02a37;
      font-size: 0.75rem;
      line-height: 1.2;
    }
  }

  .bulk-tag-panel {
    margin: 0 0 0.75rem;
    padding: 0.6rem;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #fff;

    .bulk-tag-input {
      margin-bottom: 0.5rem;
    }

    .bulk-tag-overwrite {
      margin-bottom: 0.5rem;
    }

    .bulk-divider {
      margin: 0.6rem 0;
      border: 0;
      border-top: 1px solid #e2e8f0;
    }

    .bulk-autofill-address {
      margin-bottom: 0.25rem;
    }

    .bulk-autofill-hint {
      margin-bottom: 0.5rem;
      color: #6e84a3;
      font-size: 0.72rem;
      line-height: 1.3;
    }

    .bulk-tag-actions {
      display: flex;
      align-items: center;
    }

    .bulk-error {
      color: #b02a37;
      font-size: 0.75rem;
      line-height: 1.2;
    }
  }
}
</style>
